/**
 * FR54: the discussion toggle's unread dot, for one request (#1069 P3.2), and
 * since Story 2.3 the discussion panel's CLOCK (FR69).
 *
 * ⚠️ WHY THIS EXISTS AT ALL, RATHER THAN READING THE PANEL'S OWN STATE.
 * `useCorrespondence` runs only while the panel is `visible`, and the toggle's
 * entire job is to tell you to open it. `RequestDetail` loads one request by id
 * and never goes through the list enrichment that answers the same question for
 * FR53, so it needs a summary of its own — same rules, same query, one id.
 *
 * ⚠️ IT LIVES HERE AND NOT IN `src/components/correspondence/`, AS A CHOICE:
 * the screen owns the query; the components take plain booleans (and, since
 * Story 2.3, plain ISO strings) as props. It used to be a constraint as well,
 * because that directory's suites mounted without a `QueryClientProvider`;
 * since Story 6.1 any client suite can render through `src/test-utils/render`
 * and get a provider and a settled query (`.claude/rules/testing.md`,
 * "The client timer harness: fresh legacy fakes per test, capped at 100 ms"),
 * so the placement is no longer forced (#1069 Story 6.2, 2026-09-16).
 *
 * ⚠️ DEGRADES TO NO DOT, NEVER TO AN ERROR. `retry: false` and no error
 * surface: a request-detail page must not grow an error banner because a badge
 * could not be resolved. Absence of a dot is the honest failure, and it
 * resolves the moment the user opens the panel.
 *
 * ⚠️ THE TIMER (Story 2.3, AC 1; FR69, NFR1, NFR17, AR9). While the panel is
 * open AND `usePollingEnabled()` is true (online; the tab visible on web; the
 * app not backgrounded on native, D7) the query refetches every
 * `CORRESPONDENCE_SUMMARY_POLL_MS`. Closed, offline or backgrounded:
 * `refetchInterval` is `false`. React Query re-evaluates the option on every
 * render and restarts the interval only when the RESOLVED value changes
 * (`QueryObserver.setOptions`, `nextRefetchInterval !==
 * this.#currentRefetchInterval`), so flipping `panelOpen` starts and stops
 * the timer without a remount and a re-render does not reset it.
 *
 * Cost, in words rather than as a cap (corrected by the 2026-09-11 code
 * review, which counted the route rather than trusting "three indexed
 * reads"): one poll is about five round trips on the server, the request
 * row once (it was read twice, by the claim check and again by the
 * membership projection, until that review threaded it through), the team
 * rows twice (the claim check does not hand back what it read), an identity
 * lookup and the thread rows with their read state. The read limiter
 * (`server/middleware/correspondenceRateLimit.ts`) allows on the order of
 * six hundred requests per caller per fifteen minutes per server, and a
 * mark-read counts on it too. One open panel is ninety summary reads per
 * window, plus per clock move a listing, and with a conversation open a
 * newest page and a mark-read as well: FOUR limited requests per move.
 * Ordinary use sits well under the cap; a message every tick on two tabs of
 * one request would approach it, which is why a refused or failed poll backs
 * off below rather than keeping the ten-second clock.
 */

import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from '../../services/api';
import { usePollingEnabled } from '../usePollingEnabled';

/**
 * The interval, once: 10 seconds (owner, 2026-09-04; settled, do not
 * re-open). No streaming tier and no new server process (AR9).
 */
export const CORRESPONDENCE_SUMMARY_POLL_MS = 10_000;

/**
 * The interval while the last poll FAILED with something other than a
 * revoke (code review 2026-09-11): a 429 from the read limiter, a 500, an
 * expired session. Polling a limiter you have already tripped six times a
 * minute keeps it tripped; polling a failing server at full rate helps
 * nobody. Six times the clock, and the first success restores the clock
 * (React Query re-evaluates the interval after every fetch).
 */
export const CORRESPONDENCE_SUMMARY_BACKOFF_MS =
  6 * CORRESPONDENCE_SUMMARY_POLL_MS;

/** One audience's facts, mirroring the server's `SpaceUnreadSummary`. */
export interface CorrespondenceSpaceSummary {
  hasDiscussion: boolean;
  hasUnread: boolean;
  /**
   * When anything in this audience last moved for this caller, ISO, or
   * `null` (Story 2.3, D1). Always present on the wire. The panel re-lists
   * the audience when it changes; it is activity, not unread.
   */
  lastActivityAt: string | null;
}

/** Mirrors the server's `UnreadSummary`. Both booleans are always present. */
export interface CorrespondenceSummary {
  hasDiscussion: boolean;
  hasUnread: boolean;
  /** Per space, so the panel can dot its space tabs and read its clocks. */
  spaces?: {
    internal?: CorrespondenceSpaceSummary;
    shared?: CorrespondenceSpaceSummary;
  };
}

export interface CorrespondenceSummaryOptions {
  /**
   * The discussion panel is open on this request (Story 2.3). Starts the
   * 10 s timer; nothing else in the app polls this query.
   */
  panelOpen?: boolean;
}

export const correspondenceSummaryKeys = {
  all: ['correspondence', 'summary'] as const,
  detail: (requestId: string) =>
    [...correspondenceSummaryKeys.all, requestId] as const,
};

/**
 * The summary route's one refusal body, `{ error: "Not found" }` (AR7),
 * which `api.ts` surfaces as the `ApiError`'s message.
 *
 * ⚠️ THE STATUS ALONE IS NOT ENOUGH, AND NEITHER IS THE MESSAGE (code review
 * 2026-09-11, both passes). A proxy answers an HTML 404 (message `Cannot GET
 * ...`); this server's OWN `/api/*` catch-all (`server/routes/index.ts`)
 * answers `{ error: 'Not found', code: 'ROUTE_NOT_FOUND', path }` for a
 * missing `/v1` mount, a mid-deploy window or a wrong base URL, and `api.ts`
 * lifts that `error` into the message, so message alone read a missing
 * route as "removed from the team": the panel emptied for EVERY user with no
 * error text and, on native, no way back short of a remount. The claim
 * refusal is the one 404 with this message and NO `code` (its body is
 * byte-pinned, AR7, and carries nothing else). A server older than the
 * catch-all's `code` still reads as a revoke until it is deployed.
 */
export const REVOKED_MESSAGE = 'Not found';

/**
 * "Access to this request is gone" (Story 2.3, D3; spec 7.4 constraint 3).
 * The summary route's one refusal shape is `404 { error: "Not found" }`
 * (AR7): `authorizeRequest` runs before the service, so a person removed
 * from the team mid-session gets this, NOT the empty summary the service's
 * header promises (that promise is true of the service and not of the
 * route). Not a 500, not a network failure, not a 403 (the summary never
 * answers 403). Read from the query's error by the interval callback (stop
 * asking) and by `useRequestCorrespondenceState` (show nothing).
 */
export const isAccessRevoked = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status === 404 &&
  error.message === REVOKED_MESSAGE &&
  error.code === undefined;

export const useCorrespondenceSummary = (
  requestId?: string,
  { panelOpen = false }: CorrespondenceSummaryOptions = {},
) => {
  const pollingEnabled = usePollingEnabled();

  return useQuery({
    queryKey: correspondenceSummaryKeys.detail(requestId ?? ''),
    // ⚠️ `/v1`: the read endpoints are mounted on the versioned router ONLY
    // (Story 1.2, C5). `api.ts`'s base already ends in `/api`, so this
    // resolves to `/api/v1/requests/…`; the unversioned path answers 404.
    queryFn: () =>
      api.get<CorrespondenceSummary>(
        `/v1/requests/${requestId}/correspondence/summary`,
      ),
    // ⚠️ NOT FOR OFFLINE (`o-` prefixed) ROWS. Those ids are not request ids,
    // and a stale dot is worse than none.
    enabled: Boolean(requestId) && !String(requestId).startsWith('o-'),
    // Long enough that navigating around the detail page does not re-ask, short
    // enough that returning to it after a while is fresh. The dot's real
    // freshness guarantee is the explicit invalidation on mark-read, not this.
    // The panel-open timer below refetches regardless of staleness.
    staleTime: 30 * 1000,
    retry: false,
    /**
     * In TanStack Query v5 the callback receives the full `Query`, not the
     * data (`useJobStatus.ts` records the same trap): the 404 stop reads
     * `query.state.error`. `refetchIntervalInBackground` stays at its default
     * (`false`), which is what pauses a hidden browser tab; the native pause
     * is `usePollingEnabled`'s (D7).
     */
    refetchInterval: query => {
      if (!panelOpen || !pollingEnabled) return false;
      if (isAccessRevoked(query.state.error)) return false;
      if (query.state.error) return CORRESPONDENCE_SUMMARY_BACKOFF_MS;
      return CORRESPONDENCE_SUMMARY_POLL_MS;
    },
    meta: {
      // No user-facing message: see the note above about degrading silently.
      errorMessage: undefined,
    },
  });
};
